import { Link } from "wouter";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag } from "@/components/mnf/UI";
import { useStoreQuery, queries } from "@/lib/store";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";

/**
 * MacrosCard - compact daily macros summary for /today and /nourish.
 * Renders nothing if the sister hasn't set up macros yet (silent until ready).
 */
export default function MacrosCard() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. When the signed-in user is unknown, reading
  // the seeded demo account would render Hannah's macro targets as if they
  // were this sister's own. Render nothing instead.
  const userId = me?.id ?? null;

  const plan = useStoreQuery(() => (userId ? queries.macroPlan(userId) : null));
  // Real food logs from Convex — same source Garden reads/writes
  const dateKey = new Date().toLocaleDateString("en-CA");
  const convexLogs = useConvexQuery(api.nutrition.todayLogs, { dateKey });
  const totals = (convexLogs ?? []).reduce(
    (acc, l) => ({
      kcal: acc.kcal + (l.kcal ?? 0),
      proteinG: acc.proteinG + (l.protein ?? 0),
      carbsG: acc.carbsG + (l.carbs ?? 0),
      fatG: acc.fatG + (l.fat ?? 0),
    }),
    { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 },
  );

  if (!userId || !plan) return null;

  const kcalPct = clamp01(totals.kcal / Math.max(1, plan.kcal));
  const proteinPct = clamp01(totals.proteinG / Math.max(1, plan.proteinG));
  const carbsPct = clamp01(totals.carbsG / Math.max(1, plan.carbsG));
  const fatPct = clamp01(totals.fatG / Math.max(1, plan.fatG));
  const remainingKcal = Math.max(0, plan.kcal - totals.kcal);

  return (
    <Link href="/macros" className="block no-underline">
      <GlassCard padding={16} className="hover-elevate active-elevate-2 transition-all" style={{ cursor: "pointer" }}>
        <div className="flex items-center gap-3">
          <RingMini pct={kcalPct} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="mnf-eyebrow">Today's macros</p>
              {plan.locked && plan.source === "coach" && (
                <PillTag tone="forest">From Kiley</PillTag>
              )}
            </div>
            <h3 className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 2 }}>
              {Math.round(totals.kcal)} <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", fontWeight: 500 }}>of {plan.kcal} kcal</span>
            </h3>
            <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 2 }}>
              {remainingKcal > 0 ? `${remainingKcal} kcal still ahead today` : "Today is full and faithful"}
            </p>
          </div>
          <Icon name="chevron_right" size={18} weight={500} />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <Bar label="Protein" pct={proteinPct} have={totals.proteinG} target={plan.proteinG} color="var(--mnf-fiber)" />
          <Bar label="Carbs"   pct={carbsPct}   have={totals.carbsG}   target={plan.carbsG}   color="var(--mnf-purple)" />
          <Bar label="Fat"     pct={fatPct}     have={totals.fatG}     target={plan.fatG}     color="var(--mnf-gold)" />
        </div>
      </GlassCard>
    </Link>
  );
}

function clamp01(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function RingMini({ pct }: { pct: number }) {
  const size = 56;
  const strokeWidth = 6;
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(30,74,53,0.10)" strokeWidth={strokeWidth} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="var(--mnf-f)" strokeWidth={strokeWidth} fill="none"
          strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: "none" }}>
        <span className="mnf-serif" style={{ fontSize: 13, fontWeight: 500, color: "var(--mnf-fd)" }}>
          {Math.round(pct * 100)}%
        </span>
      </div>
    </div>
  );
}

function Bar({
  label, pct, have, target, color,
}: {
  label: string; pct: number; have: number; target: number; color: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between" style={{ marginBottom: 3 }}>
        <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9 }}>{label}</span>
        <span className="mnf-body" style={{ fontSize: 10.5, fontWeight: 700, color: "var(--mnf-fd)" }}>
          {Math.round(have)}<span style={{ color: "var(--mnf-tg)", fontWeight: 500 }}>/{target}g</span>
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "rgba(30,74,53,0.08)", overflow: "hidden" }}>
        <div
          style={{
            width: `${Math.max(2, pct * 100)}%`,
            height: "100%",
            background: color,
            transition: "width 0.5s ease",
            borderRadius: 999,
          }}
        />
      </div>
    </div>
  );
}
